import React, { useEffect, useMemo, useState } from 'react';
import pipefitterApi from '../../../services/pipefitterApi';
import { fetchPipeStatusListUnpaginated } from '../api/pipeStatusList';
import { pipefitterPipeBasedGenerateSequence } from '../../../redux/Pipefitter/actions';
import { configurationConstants } from '../constants/configurationConstants';
import type { Spread, SubSpread } from '../types';
import type { GenerateSequenceFormValues, PipeSelectionItem, PipeSpecificationForSequence } from './GenerateSequenceModal';
import { GenerateSequenceModal } from './GenerateSequenceModal';

interface UpdateSequenceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectNumber?: string;
  projectId?: string | number | null;
  projectName?: string;
  /** Material line pipe id (getPipeStatusList pipe_id) */
  materialPipeId?: string | null;
  selectionType: string;
  selectedConfiguration: any;
  filteredConfigurationList: Spread[];
  sequenceList: any[];
  isSubmitting?: boolean;
  dispatch: any;
  onSuccess?: () => void;
  pipeSpecification?: PipeSpecificationForSequence | null;
}

export const UpdateSequenceModal: React.FC<UpdateSequenceModalProps> = ({
  open,
  onOpenChange,
  projectNumber,
  projectId,
  projectName = '',
  materialPipeId,
  selectionType,
  selectedConfiguration,
  filteredConfigurationList,
  sequenceList,
  isSubmitting = false,
  dispatch,
  onSuccess,
  pipeSpecification = null,
}) => {
  const [fetchedPipeRows, setFetchedPipeRows] = useState<any[]>([]);

  useEffect(() => {
    if (!open) {
      setFetchedPipeRows([]);
      return;
    }
    const pipeId = materialPipeId;
    if ((projectNumber == null || projectNumber === '') && (projectId == null || projectId === '')) return;
    if (pipeId == null || pipeId === '') return;

    let cancelled = false;
    fetchPipeStatusListUnpaginated({
      project_id: projectId,
      project_num: projectNumber,
      project_name: projectName,
      pipe_id: pipeId,
    })
      .then((rows) => {
        if (!cancelled) setFetchedPipeRows(rows as any[]);
      })
      .catch(() => {
        if (!cancelled) setFetchedPipeRows([]);
      });

    return () => {
      cancelled = true;
    };
  }, [open, projectId, projectNumber, projectName, materialPipeId]);

  const allPipeSelections = useMemo(() => {
    const seen = new Set<string>();
    const normalized: Array<{ pipeID: string; sequence?: string; status?: string }> = [];
    (fetchedPipeRows || []).forEach((pipe: any) => {
      const pipeID = String(pipe?.pipeID ?? '').trim();
      if (!pipeID || seen.has(pipeID)) return;
      seen.add(pipeID);
      normalized.push({
        pipeID,
        sequence: pipe?.spread === 'Any' ? 'Unassigned' : pipe?.spread,
        status: pipe?.status,
      });
    });
    return normalized;
  }, [fetchedPipeRows]);

  const [selectedPipeIds, setSelectedPipeIds] = useState<string[]>([]);
  const [prefillValues, setPrefillValues] = useState<Partial<GenerateSequenceFormValues> | undefined>(undefined);

  const usedPipeIds = useMemo(
    () =>
      new Set(
        (sequenceList || [])
          .map((row: any) => String(row?.pipeId ?? '').trim())
          .filter((id: string) => id.length > 0)
      ),
    [sequenceList]
  );

  const pipeSelectionItems = useMemo<PipeSelectionItem[]>(
    () =>
      (allPipeSelections || []).map((pipe) => ({
        id: pipe.pipeID,
        label: pipe.pipeID,
        sequence: pipe.sequence,
        status: pipe.status,
        subLabel: pipe.sequence ? `Current sequence: ${pipe.sequence}` : undefined,
        disabled: usedPipeIds.has(pipe.pipeID),
        disabledReason: usedPipeIds.has(pipe.pipeID)
          ? 'This pipe has already been used for sequence'
          : undefined,
      })),
    [allPipeSelections, usedPipeIds]
  );

  useEffect(() => {
    if (!open) return;
    setSelectedPipeIds(
      (allPipeSelections || [])
        .map((pipe) => pipe.pipeID)
        .filter((pipeId) => !usedPipeIds.has(pipeId))
    );
  }, [open, allPipeSelections, usedPipeIds]);

  useEffect(() => {
    if (!open || !selectedConfiguration?.id || !projectNumber) return;
    let cancelled = false;
    const params = new URLSearchParams({
      projectNumber,
      spreadId:
        selectionType === configurationConstants.selectionType.spread
          ? String(selectedConfiguration.id)
          : String(
              selectedConfiguration?.spreadId ??
                filteredConfigurationList.find((s: Spread) =>
                  s.subSpreads?.some((sub: SubSpread) => sub.id === selectedConfiguration?.id)
                )?.id ??
                ''
            ),
    });
    if (selectionType === configurationConstants.selectionType.subSpread) {
      params.set('subSpreadId', String(selectedConfiguration.id));
    }

    pipefitterApi
      .get(`/sequence/prefill?${params.toString()}`)
      .then((res: any) => {
        if (cancelled) return;
        const prefill = res?.data?.data?.prefill;
        if (!prefill) {
          setPrefillValues(undefined);
          return;
        }
        setPrefillValues({
          mode: 'append',
          sequenceName: prefill.sequenceName ?? '',
          nominalPipeLengthFt: Number(prefill.nominalPipeLengthFt ?? 40),
          targetNumberOfPipes: Number(prefill.targetNumberOfPipes ?? 24),
          targetPipelineLengthFt: Number(prefill.targetPipelineLengthFt ?? 960),
          nominalODMm: Number(prefill.nominalODmm ?? 208),
          exclusion6OClockMm: Number(prefill.exclusion6OClockMm ?? 36),
          weldSeamExclusionMm: Number(prefill.weldSeamExclusionMm ?? 18),
          hiloThresholdMm: Number(prefill.hiLoThresholdMm ?? 1.5),
          pipeMaterial: prefill.pipeMaterial ?? 'Iron',
          pipeType: String(prefill.pipeType ?? '').toLowerCase() === 'double' ? 'double' : 'single',
          pipeRotation:
            String(prefill.pipeRotation ?? '').toLowerCase().includes('clock') &&
            !String(prefill.pipeRotation ?? '').toLowerCase().includes('anti')
              ? 'clockwise'
              : String(prefill.pipeRotation ?? '').toLowerCase().includes('anti')
                ? 'antiClockwise'
                : 'any',
          fitQuality: String(prefill.fitQuality ?? '').toLowerCase() === 'acceptable' ? 'acceptable' : 'optimal',
          allowPipeTranslation: Boolean(prefill.allowPipeTranslation),
        });
      })
      .catch(() => {
        if (!cancelled) setPrefillValues(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedConfiguration?.id, selectionType, projectNumber, filteredConfigurationList]);

  const updateModalTitle = useMemo(() => {
    const name =
      selectionType === configurationConstants.selectionType.spread
        ? String(selectedConfiguration?.spreadName ?? '').trim()
        : String(selectedConfiguration?.subSpreadName ?? '').trim();
    return name.length > 0 ? `Update Sequence – ${name}` : 'Update Sequence';
  }, [selectionType, selectedConfiguration?.spreadName, selectedConfiguration?.subSpreadName]);

  return (
    <GenerateSequenceModal
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={(values) => {
        if (!projectNumber) return;
        const payload = {
          projectNumber,
          mode: 'APPEND',
          sequenceName: values.sequenceName,
          pipeIds: selectedPipeIds,
          ...(values.existingSequenceRef && Object.keys(values.existingSequenceRef).length > 0
            ? { existingSequenceRef: values.existingSequenceRef }
            : {}),
          config: values.config,
          ...(values.notifyEmail ? { notifyEmail: values.notifyEmail } : {}),
          ...(values.notifyEmails && values.notifyEmails.length > 0
            ? { notifyEmails: values.notifyEmails }
            : {}),
        };
        dispatch(
          pipefitterPipeBasedGenerateSequence(
            payload,
            () => {
              onOpenChange(false);
              onSuccess?.();
            },
            () => {}
          )
        );
      }}
      isSubmitting={isSubmitting}
      initialValues={prefillValues}
      selectedPipeCount={selectedPipeIds.length}
      pipeIds={selectedPipeIds}
      sequences={
        selectionType === configurationConstants.selectionType.spread && selectedConfiguration?.id
          ? [{ label: selectedConfiguration.spreadName, value: `spread:${selectedConfiguration.id}`, sequenceName: selectedConfiguration.spreadName ?? '' }]
          : selectionType === configurationConstants.selectionType.subSpread && selectedConfiguration?.id
            ? [{ label: selectedConfiguration.subSpreadName, value: `subSpread:${selectedConfiguration.id}`, sequenceName: selectedConfiguration.subSpreadName ?? '' }]
            : []
      }
      title={updateModalTitle}
      submitLabel="Update Sequence"
      modeOverride="append"
      lockModeSelection
      sequenceModeOptions="append-only"
      existingSequenceValue={
        selectionType === configurationConstants.selectionType.spread
          ? `spread:${selectedConfiguration?.id ?? ''}`
          : `subSpread:${selectedConfiguration?.id ?? ''}`
      }
      pipeSpecification={pipeSpecification}
      syncMetricsFromPipeSelection
      applyInitialValuesWhileOpen
      pipeSelectionItems={pipeSelectionItems}
      selectedPipeIds={selectedPipeIds}
      onSelectedPipeIdsChange={setSelectedPipeIds}
      simplePipeSelectionTable
    />
  );
};

